import { useEffect, useRef } from "react";
import moment from "moment-timezone";
import { ShipmentStopType } from "@api/graphql/generated/generated-types";
import { momentToTimePickerDate } from "@src/common/utils/timezoneConverters";
import {
  CustomRecurrenceScheduleType,
  ExtendedRecurrenceScheduleType,
} from "@src/orders/create/utils/CreateOrderContextProvider";

interface UseDefaultPickupReadyTimeProps {
  fieldName: string;
  stopType: ShipmentStopType;
  recurrenceSchedule: ExtendedRecurrenceScheduleType;
  getValue: (fieldName: string) => unknown;
  setValue: (fieldName: string, value: unknown) => void;
}

interface ReadyTimeInputs {
  stopType: ShipmentStopType;
  recurrenceSchedule: ExtendedRecurrenceScheduleType;
}

export function useDefaultPickupReadyTime({
  fieldName,
  stopType,
  recurrenceSchedule,
  getValue,
  setValue,
}: UseDefaultPickupReadyTimeProps): void {
  const previousInputs = useRef<ReadyTimeInputs | null>(null);

  useEffect(() => {
    const shouldUseDefault =
      stopType === ShipmentStopType.PickUp &&
      recurrenceSchedule === CustomRecurrenceScheduleType.DoesNotRepeat;
    const previous = previousInputs.current;
    previousInputs.current = { stopType, recurrenceSchedule };

    if (previous === null) {
      if (shouldUseDefault && !getValue(fieldName)) {
        setValue(fieldName, momentToTimePickerDate(moment()));
      }
      return;
    }

    const didInputsChange =
      previous.stopType !== stopType ||
      previous.recurrenceSchedule !== recurrenceSchedule;
    if (!didInputsChange) return;

    setValue(
      fieldName,
      shouldUseDefault ? momentToTimePickerDate(moment()) : "",
    );
  }, [fieldName, getValue, recurrenceSchedule, setValue, stopType]);
}
